'use client';

import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { ArrowDownToLine, ArrowUpFromLine, Download } from 'lucide-react';

import type { PriorAuthorizationAttachmentT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { Button } from '@/shared/ui/shadcn/button';
import { Label } from '@/shared/ui/shadcn/label';
import { logger } from '@/shared/lib';

import { downloadPriorAuthAttachment } from '../api/priorAuthApi';

function formatDate(raw: string | null | undefined): string {
  if (!raw) return '';
  try {
    return new Date(raw).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return raw;
  }
}

export function PriorAuthAttachmentsList({
  caseId,
  attachments,
}: {
  caseId: number;
  attachments: PriorAuthorizationAttachmentT[];
}) {
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const handleDownload = useCallback(
    async (att: PriorAuthorizationAttachmentT) => {
      const attachmentId = att.AttachmentId;
      if (attachmentId == null) return;

      setDownloadingId(attachmentId);
      try {
        const blob = await downloadPriorAuthAttachment(caseId, attachmentId);
        const fileName = att.FileName || `attachment_${attachmentId}`;
        const objectUrl = URL.createObjectURL(blob);
        try {
          const anchor = document.createElement('a');
          anchor.href = objectUrl;
          anchor.download = fileName;
          document.body.appendChild(anchor);
          anchor.click();
          anchor.remove();
        } finally {
          setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        toast.error('Download failed', { description: message });
        logger.error('priorAuth.downloadAttachment.failed', { caseId, attachmentId, err });
      } finally {
        setDownloadingId(null);
      }
    },
    [caseId]
  );

  if (attachments.length === 0) return null;

  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        Attachments ({attachments.length})
      </Label>
      <ul className="space-y-1">
        {attachments.map((att) => {
          const isOutbound = att.IsOutbound === true;
          const Icon = isOutbound ? ArrowUpFromLine : ArrowDownToLine;
          const canDownload = att.AttachmentId != null;
          const isDownloading = downloadingId === att.AttachmentId;
          return (
            <li
              key={att.AttachmentId ?? att.FileName}
              className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-2.5 py-1.5"
            >
              <Icon
                className={`h-3.5 w-3.5 flex-none ${isOutbound ? 'text-muted-foreground' : 'text-blue-500 dark:text-blue-400'}`}
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium">
                  {att.FileName || 'Untitled attachment'}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {isOutbound ? 'Sent to payer' : 'From payer'}
                  {att.CreatedDateStamp ? ` · ${formatDate(att.CreatedDateStamp)}` : ''}
                </p>
              </div>
              {canDownload ? (
                <Button
                  size="icon-sm"
                  variant="ghost"
                  className="h-6 w-6 flex-none"
                  onClick={() => void handleDownload(att)}
                  disabled={isDownloading}
                  title="Download attachment"
                  aria-label={`Download ${att.FileName || 'attachment'}`}
                >
                  <Download className="h-3.5 w-3.5" aria-hidden />
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
